// Start page (feature catalogue A1–A3, A5): the list of boards (GET /api/boards) with the tabs
// "Boards" / "Archiv" / "Papierkorb", search, sort, tiles or list, favourites (per browser) and "new
// board" with a template. A board exists as soon as someone edits it; a new board is opened with its
// title and template in the URL and the app sets both up on first connect. Deleted boards stay in
// the trash for 30 days (GET /api/trash) and can be restored by their admins.
import { api, ApiError } from "./account";

export interface BoardSummary { id: string; title: string; cards: number; frames: number; updated: number; archived?: boolean; members?: number; kind?: Kind }
/** Project board or team board (doc 13). */
export type Kind = "project" | "team";
export const KIND_NAMES: Record<Kind, string> = { project: "Projekt", team: "Team" };
export interface TrashEntry { id: string; title: string; cards: number; deletedAt: number; deletedBy: string | null; purgeAt: number }
type Tab = "active" | "archived" | "trash";
const TAB_NAMES: Record<Tab, string> = { active: "Boards", archived: "Archiv", trash: "Papierkorb" };

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

/** Board id from a title: readable slug + random suffix ("Projekt Orion" → "projekt-orion-k3f9"). */
export function boardId(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
    .slice(0, 40).replace(/-+$/g, "");
  const rnd = Math.random().toString(36).slice(2, 6).padEnd(4, "0");
  return slug ? `${slug}-${rnd}` : `board-${rnd}`;
}

const rel = new Intl.RelativeTimeFormat("de", { numeric: "auto" });
const abs = new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
export function changed(ms: number, now = Date.now()): string {
  const s = Math.round((ms - now) / 1000);
  if (s > -60) return "gerade eben";
  if (s > -3600) return rel.format(Math.round(s / 60), "minute");
  if (s > -86400) return rel.format(Math.round(s / 3600), "hour");
  if (s > -7 * 86400) return rel.format(Math.round(s / 86400), "day");
  return abs.format(new Date(ms));
}

// per-browser settings (favourites, tiles or list, kind filter)
const FAVS = "lanewerk.favourites", VIEW = "lanewerk.home.view", KINDS = "lanewerk.home.kind";
function readJson<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v ? (JSON.parse(v) as T) : fallback; } catch { return fallback; }
}
function writeJson(key: string, v: unknown) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* not kept */ }
}

export class Home {
  private boards: BoardSummary[] = [];
  private trash: TrashEntry[] = [];
  private grid = $("#lw-home-grid");
  private search = $<HTMLInputElement>("#lw-home-search");
  private sort = $<HTMLSelectElement>("#lw-home-sort");
  private status = $("#lw-home-status");
  private tab: Tab = "active";
  private view: "grid" | "list" = readJson<string>(VIEW, "grid") === "list" ? "list" : "grid";
  private favs = new Set<string>(readJson<string[]>(FAVS, []));
  /** "" = project and team boards. */
  private kind: Kind | "" = ((k) => (k === "project" || k === "team" ? k : ""))(readJson<string>(KINDS, ""));
  private menu: HTMLElement | null = null;

  constructor() {
    document.title = "Boards – Lanewerk";
    this.search.addEventListener("input", () => this.render());
    this.sort.addEventListener("change", () => this.render());
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-home-tabs [data-tab]")) {
      b.addEventListener("click", () => { this.tab = (b.dataset.tab as Tab) in TAB_NAMES ? (b.dataset.tab as Tab) : "active"; this.render(); });
    }
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-home-kinds [data-kind]")) {
      b.addEventListener("click", () => { const k = b.dataset.kind; this.kind = k === "project" || k === "team" ? k : ""; writeJson(KINDS, this.kind); this.render(); });
    }
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-home-view [data-view]")) {
      b.addEventListener("click", () => { this.view = b.dataset.view === "list" ? "list" : "grid"; writeJson(VIEW, this.view); this.render(); });
    }
    $("#lw-home-new").addEventListener("submit", (e) => {
      e.preventDefault();
      const input = $<HTMLInputElement>("#lw-home-title");
      const title = input.value.trim();
      if (!title) return input.focus();
      const template = $<HTMLSelectElement>("#lw-home-template").value;
      const kind = $<HTMLSelectElement>("#lw-home-kind").value === "team" ? "&kind=team" : "";
      location.href = `?board=${encodeURIComponent(boardId(title))}&title=${encodeURIComponent(title)}&template=${encodeURIComponent(template)}${kind}`;
    });
    document.addEventListener("pointerdown", (e) => { if (this.menu && !this.menu.contains(e.target as Node)) this.closeMenu(); });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") this.closeMenu(); });
    void this.load();
  }

  async load() {
    try {
      [this.boards, this.trash] = await Promise.all([
        api<BoardSummary[]>("./api/boards"),
        api<TrashEntry[]>("./api/trash").catch(() => [] as TrashEntry[]),
      ]);
      this.status.textContent = "";
    } catch (e) {
      this.status.classList.remove("ok");
      this.status.textContent = `Die Board-Liste konnte nicht geladen werden (${(e as Error).message}).`;
    }
    this.render();
  }

  private render() {
    const q = this.search.value.trim().toLowerCase();
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-home-tabs [data-tab]")) {
      const tab = b.dataset.tab as Tab;
      b.setAttribute("aria-pressed", String(tab === this.tab));
      const n = tab === "trash" ? this.trash.length : this.boards.filter((x) => !!x.archived === (tab === "archived")).length;
      b.textContent = `${TAB_NAMES[tab]}${n ? ` (${n})` : ""}`;
    }
    const active = this.boards.filter((b) => !b.archived).length;
    $("#lw-home-count").textContent = active === 1 ? "1 Board" : `${active} Boards`;
    $("#lw-home-new").hidden = this.tab !== "active";
    $("#lw-home-notrash").hidden = !(this.tab === "trash" && this.trash.length === 0);
    $("#lw-home-kinds").hidden = this.tab === "trash"; // trashed boards have no kind to filter by
    if (this.tab === "trash") {
      $("#lw-home-nokind").hidden = true;
      const hits = this.trash.filter((t) => !q || (t.title || t.id).toLowerCase().includes(q));
      $(".tiles").classList.toggle("as-list", this.view === "list");
      this.grid.replaceChildren(...hits.map((t) => this.trashTile(t)));
      $("#lw-home-empty").hidden = true;
      $("#lw-home-noarchive").hidden = true;
      $("#lw-home-nohit").hidden = !(this.trash.length > 0 && hits.length === 0);
      return;
    }
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-home-kinds [data-kind]")) b.setAttribute("aria-pressed", String((b.dataset.kind ?? "") === this.kind));
    const kindOf = (b: BoardSummary): Kind => (b.kind === "team" ? "team" : "project");
    const tabAll = this.boards.filter((b) => !!b.archived === (this.tab === "archived"));
    const inTab = tabAll.filter((b) => !this.kind || kindOf(b) === this.kind);
    const byName = (a: BoardSummary, b: BoardSummary) => (a.title || a.id).localeCompare(b.title || b.id, "de");
    const list = inTab
      .filter((b) => !q || (b.title || b.id).toLowerCase().includes(q))
      .sort((a, b) =>
        // favourites first, then the chosen order
        Number(this.favs.has(b.id)) - Number(this.favs.has(a.id)) ||
        (this.sort.value === "name" ? byName(a, b) : b.updated - a.updated));
    const grid = $(".tiles");
    grid.classList.toggle("as-list", this.view === "list");
    this.grid.replaceChildren(...list.map((b) => this.tile(b)));
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-home-view [data-view]")) b.setAttribute("aria-pressed", String(b.dataset.view === this.view));
    $("#lw-home-empty").hidden = !(this.tab === "active" && active === 0);
    $("#lw-home-noarchive").hidden = !(this.tab === "archived" && tabAll.length === 0);
    $("#lw-home-nohit").hidden = !(inTab.length > 0 && list.length === 0);
    const nokind = $("#lw-home-nokind");
    nokind.hidden = !(this.kind && inTab.length === 0 && tabAll.length > 0);
    nokind.textContent = this.kind === "team" ? "Keine Team-Boards. Mach ein Board über ⋯ zum Team-Board oder lege ein neues als Team-Board an." : "Keine Projekt-Boards.";
  }

  /** A board in the trash: when and by whom it was deleted, when it goes for good, restore. */
  private trashTile(t: TrashEntry): HTMLElement {
    const box = el("div", "tile-box trashed");
    const tile = el("div", "tile");
    tile.appendChild(el("span", "tile-title", t.title || t.id));
    const by = t.deletedBy ? ` von ${t.deletedBy}` : "";
    tile.appendChild(el("span", "tile-meta", `${t.cards} ${t.cards === 1 ? "Karte" : "Karten"} · gelöscht ${changed(t.deletedAt)}${by}`));
    const gone = el("span", "tile-when", `Endgültig weg am ${abs.format(new Date(t.purgeAt))}`);
    tile.appendChild(gone);
    const restore = el("button", "tile-restore", "Wiederherstellen");
    restore.type = "button";
    restore.setAttribute("aria-label", `${t.title || t.id} wiederherstellen`);
    restore.addEventListener("click", () => void this.restore(t));
    tile.appendChild(restore);
    box.appendChild(tile);
    return box;
  }

  private tile(b: BoardSummary): HTMLElement {
    const box = el("div", "tile-box");
    const a = el("a", "tile");
    a.href = `?board=${encodeURIComponent(b.id)}`;
    a.appendChild(el("span", "tile-title", b.title || b.id));
    const parts = [KIND_NAMES[b.kind === "team" ? "team" : "project"], `${b.cards} ${b.cards === 1 ? "Karte" : "Karten"}`, `${b.frames} Rahmen`];
    if (b.members) parts.push(`${b.members} ${b.members === 1 ? "Mitglied" : "Mitglieder"}`);
    a.appendChild(el("span", "tile-meta", parts.join(" · ")));
    const when = el("span", "tile-when", `Geändert ${changed(b.updated)}`);
    when.title = new Date(b.updated).toLocaleString("de-CH");
    a.appendChild(when);
    const fav = this.favs.has(b.id);
    if (fav) box.classList.add("fav");
    const star = el("button", "tile-icon tile-star", fav ? "★" : "☆");
    star.type = "button";
    star.title = fav ? "Aus den Favoriten entfernen" : "Als Favorit oben anzeigen";
    star.setAttribute("aria-label", `${star.title}: ${b.title || b.id}`);
    star.setAttribute("aria-pressed", String(fav));
    star.addEventListener("click", () => {
      if (this.favs.has(b.id)) this.favs.delete(b.id); else this.favs.add(b.id);
      writeJson(FAVS, [...this.favs]);
      this.render();
    });
    const more = el("button", "tile-icon tile-more", "⋯");
    more.type = "button";
    more.title = "Weitere Aktionen";
    more.setAttribute("aria-label", `Aktionen für ${b.title || b.id}`);
    more.setAttribute("aria-haspopup", "menu");
    more.addEventListener("click", (e) => { e.stopPropagation(); this.openMenu(b, more); });
    box.append(a, star, more);
    return box;
  }

  private closeMenu() {
    this.menu?.remove();
    this.menu = null;
  }

  private openMenu(b: BoardSummary, anchor: HTMLElement) {
    this.closeMenu();
    const m = el("div", "menu tile-menu");
    m.setAttribute("role", "menu");
    const item = (label: string, fn: () => void, cls = "") => {
      const x = el("button", `menu-action ${cls}`, label);
      x.type = "button";
      x.setAttribute("role", "menuitem");
      x.addEventListener("click", () => { this.closeMenu(); fn(); });
      m.appendChild(x);
    };
    item("Öffnen", () => { location.href = `?board=${encodeURIComponent(b.id)}`; });
    item(b.archived ? "Wiederherstellen" : "Archivieren", () => void this.archive(b, !b.archived));
    item(b.kind === "team" ? "Zum Projekt-Board machen" : "Zum Team-Board machen", () => void this.setKind(b, b.kind === "team" ? "project" : "team"));
    const del = el("button", "menu-action danger", "In den Papierkorb …");
    del.type = "button";
    del.id = "lw-home-delete";
    del.setAttribute("role", "menuitem");
    del.addEventListener("click", () => {
      // one more step: say what happens, then do it
      m.replaceChildren();
      m.appendChild(el("p", "menu-hint", `„${b.title || b.id}“ in den Papierkorb legen? Wer es offen hat, wird getrennt. 30 Tage lang lässt es sich wiederherstellen, danach ist es endgültig gelöscht.`));
      const row = el("div", "menu-row");
      const yes = el("button", "danger-solid", "In den Papierkorb");
      yes.type = "button";
      yes.id = "lw-home-delete-yes";
      yes.addEventListener("click", () => { this.closeMenu(); void this.remove(b); });
      const no = el("button", "plain-action", "Abbrechen");
      no.type = "button";
      no.addEventListener("click", () => this.closeMenu());
      row.append(yes, no);
      m.appendChild(row);
      yes.focus();
    });
    m.appendChild(del);
    document.body.appendChild(m);
    const r = anchor.getBoundingClientRect();
    m.style.position = "fixed";
    m.style.transform = `translate(${Math.max(8, Math.min(r.right - 260, window.innerWidth - 268))}px, ${r.bottom + 6}px)`;
    this.menu = m;
    m.querySelector<HTMLButtonElement>("button")?.focus();
  }

  /** A confirmation (not an error) in the status line. */
  private say(text: string) {
    this.status.textContent = text;
    this.status.classList.add("ok");
  }

  private async remove(b: BoardSummary) {
    try {
      await api(`./api/boards/${encodeURIComponent(b.id)}`, "DELETE");
      await this.load();
      this.say(`„${b.title || b.id}“ liegt jetzt im Papierkorb.`);
    } catch (e) {
      this.status.classList.remove("ok");
      this.status.textContent = e instanceof ApiError && e.status === 403
        ? "In den Papierkorb legen kann nur, wer dieses Board verwaltet."
        : `Nicht gelöscht (${(e as Error).message}).`;
    }
  }

  private async restore(t: TrashEntry) {
    try {
      await api(`./api/trash/${encodeURIComponent(t.id)}/restore`, "POST");
      await this.load();
      this.say(`„${t.title || t.id}“ ist wieder da.`);
    } catch (e) {
      this.status.classList.remove("ok");
      this.status.textContent = `Nicht wiederhergestellt (${(e as Error).message}).`;
    }
  }

  private async setKind(b: BoardSummary, kind: Kind) {
    try {
      await api(`./api/boards/${encodeURIComponent(b.id)}/kind`, "PUT", { kind });
      b.kind = kind;
      this.render();
      this.say(`„${b.title || b.id}“ ist jetzt ein ${KIND_NAMES[kind]}-Board.`);
    } catch (e) {
      this.status.classList.remove("ok");
      this.status.textContent = e instanceof ApiError && e.status === 403
        ? "Die Art ändern kann nur, wer dieses Board verwaltet."
        : `Nicht geändert (${(e as Error).message}).`;
    }
  }

  private async archive(b: BoardSummary, archived: boolean) {
    try {
      await api(`./api/boards/${encodeURIComponent(b.id)}/archived`, "PUT", { archived });
      b.archived = archived;
      this.status.textContent = "";
      this.render();
    } catch (e) {
      this.status.classList.remove("ok");
      this.status.textContent = e instanceof ApiError && e.status === 403
        ? "Archivieren kann nur, wer dieses Board verwaltet."
        : `Nicht geändert (${(e as Error).message}).`;
    }
  }
}
